class TmpBucketedCollection {
  constructor(root) {
    this.root = root;
    this.container = root.closest('#ProductGridContainer');
    this.mode = root.dataset.bucketedMode || 'bucket';
    this.pageSize = Number(root.dataset.pageSize || 24);
    this.sectionId = root.dataset.sectionId;
    this.sourcePath = root.dataset.sourceCollectionPath || window.location.pathname;
    this.availablePath = root.dataset.availableCollectionPath;
    this.soldOutPath = root.dataset.soldOutCollectionPath;
    this.renderToken = 0;
    this.bucketCache = new Map();
    this.nativeItems = [];
    this.mutationObserver = new MutationObserver(() => this.scheduleRender());
    this.loadMoreObserver = undefined;
  }

  connect() {
    if (!this.container || !this.sectionId) return;
    if (this.mode !== 'native' && (!this.availablePath || !this.soldOutPath)) return;
    this.scheduleRender();
    this.mutationObserver.observe(this.container, { childList: true, subtree: true });
    window.addEventListener('popstate', () => this.scheduleRender());
    document.addEventListener('tmp:facets-rendered', () => this.scheduleRender());
  }

  scheduleRender() {
    if (this.rendering || this.loadingMore) return;
    window.clearTimeout(this.timer);
    this.timer = window.setTimeout(() => this.render(), 30);
  }

  async render() {
    if (this.container.dataset.bucketedCollectionState === 'rendering') return;

    const token = ++this.renderToken;
    const params = this.currentParams();
    const renderKey = `${window.location.pathname}?${params.toString()}`;
    if (this.lastRenderedKey === renderKey && this.container.dataset.bucketedCollectionState === 'ready') return;

    this.rendering = true;
    this.disconnectLoadMore();
    this.bucketCache.clear();
    this.loadedCount = 0;
    this.totalCount = 0;
    this.availableCount = 0;
    this.soldOutCount = 0;
    this.nativeItems = [];
    this.container.dataset.bucketedCollectionState = 'rendering';

    try {
      if (this.mode === 'native') {
        const nativeCollection = await this.fetchNativeCollection(params);
        if (token !== this.renderToken) return;
        if (!nativeCollection) return;

        this.nativeItems = nativeCollection.items;
        this.availableCount = nativeCollection.availableCount;
        this.soldOutCount = nativeCollection.soldOutCount;
        this.totalCount = this.nativeItems.length;
      } else {
        const [availableFirst, soldOutFirst] = await Promise.all([
          this.fetchBucket(this.availablePath, params, 1),
          this.fetchBucket(this.soldOutPath, params, 1),
        ]);
        if (token !== this.renderToken) return;
        if (!availableFirst || !soldOutFirst) return;

        this.availableCount = availableFirst.count;
        this.soldOutCount = soldOutFirst.count;
        this.totalCount = this.availableCount + this.soldOutCount;
      }

      const items = await this.getVirtualItems(0, this.pageSize, params);
      if (token !== this.renderToken) return;

      this.paintInitial(items, this.totalCount);
      this.loadedCount = items.length;
      this.lastRenderedKey = renderKey;
      this.container.dataset.bucketedCollectionState = 'ready';
      this.updateLoadMoreState();
    } catch (error) {
      this.container.dataset.bucketedCollectionState = 'fallback';
      console.warn('Bucketed collection fallback:', error);
    } finally {
      this.rendering = false;
    }
  }

  currentParams() {
    const params = new URLSearchParams(window.location.search);
    params.delete('page');
    return params;
  }

  async getVirtualItems(offset, limit, params) {
    if (this.mode === 'native') {
      return this.nativeItems.slice(offset, offset + limit);
    }

    const items = [];

    if (offset < this.availableCount) {
      const availableItems = await this.getBucketSlice(
        this.availablePath,
        params,
        offset,
        Math.min(limit, this.availableCount - offset)
      );
      items.push(...availableItems);
    }

    if (items.length < limit) {
      const soldOutOffset = Math.max(offset - this.availableCount, 0);
      const soldOutLimit = Math.min(limit - items.length, this.soldOutCount - soldOutOffset);
      if (soldOutLimit > 0) {
        items.push(...(await this.getBucketSlice(this.soldOutPath, params, soldOutOffset, soldOutLimit)));
      }
    }

    return items;
  }

  async fetchNativeCollection(params) {
    const firstPage = await this.fetchNativePage(params, 1);
    if (!firstPage) return null;

    const pageCount = Math.ceil(firstPage.count / this.pageSize);
    const pages = [firstPage];
    if (pageCount > 1) {
      const remainingPages = await Promise.all(
        Array.from({ length: pageCount - 1 }, (_, index) => this.fetchNativePage(params, index + 2))
      );
      if (remainingPages.some((page) => !page)) return null;
      pages.push(...remainingPages);
    }

    const items = pages.flatMap((page) => page.items);
    const availableItems = items.filter((item) => this.isItemAvailable(item));
    const soldOutItems = items.filter((item) => !this.isItemAvailable(item));

    return {
      availableCount: availableItems.length,
      soldOutCount: soldOutItems.length,
      items: [...availableItems, ...soldOutItems],
    };
  }

  async fetchNativePage(params, page) {
    const cacheKey = `native:${this.sourcePath}?${params.toString()}|${page}`;
    if (this.bucketCache.has(cacheKey)) return this.bucketCache.get(cacheKey);

    const url = new URL(this.sourcePath, window.location.origin);
    params.forEach((value, key) => url.searchParams.append(key, value));
    url.searchParams.set('page', String(page));
    url.searchParams.set('section_id', this.sectionId);

    const response = await fetch(url.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    if (!response.ok) return null;

    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const grid = doc.querySelector('#product-grid');
    const countNode = doc.querySelector('#ProductCountDesktop') || doc.querySelector('#ProductCount');
    if (!grid || !countNode) return null;

    const nativePage = {
      count: this.parseCount(countNode.textContent),
      items: Array.from(grid.children).filter((item) => item.classList.contains('grid__item')),
    };
    this.bucketCache.set(cacheKey, nativePage);
    return nativePage;
  }

  isItemAvailable(item) {
    if (item.dataset.productAvailable) return item.dataset.productAvailable === 'true';

    return !Array.from(item.querySelectorAll('.tmp-card-soldout, .badge')).some((node) =>
      (node.textContent || '').trim().toLowerCase().includes('sold out')
    );
  }

  async getBucketSlice(path, params, offset, limit) {
    const items = [];
    let page = Math.floor(offset / this.pageSize) + 1;
    let start = offset % this.pageSize;

    while (items.length < limit) {
      const bucket = await this.fetchBucket(path, params, page);
      if (!bucket) return items;

      const nextItems = bucket.items.slice(start, start + (limit - items.length));
      items.push(...nextItems);
      if (nextItems.length === 0 || bucket.items.length < this.pageSize) break;

      page += 1;
      start = 0;
    }

    return items;
  }

  async fetchBucket(path, params, page) {
    const cacheKey = `${path}?${params.toString()}|${page}`;
    if (this.bucketCache.has(cacheKey)) return this.bucketCache.get(cacheKey);

    const url = new URL(path, window.location.origin);
    params.forEach((value, key) => url.searchParams.append(key, value));
    url.searchParams.set('page', String(page));
    url.searchParams.set('section_id', this.sectionId);

    const response = await fetch(url.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    if (!response.ok) return null;

    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const grid = doc.querySelector('#product-grid');
    const countNode = doc.querySelector('#ProductCountDesktop') || doc.querySelector('#ProductCount');
    if (!grid || !countNode) return null;

    const bucket = {
      count: this.parseCount(countNode.textContent),
      items: Array.from(grid.children).filter((item) => item.classList.contains('grid__item')),
    };
    this.bucketCache.set(cacheKey, bucket);
    return bucket;
  }

  parseCount(text) {
    const match = (text || '').replace(/,/g, '').match(/\d+/);
    return match ? Number(match[0]) : 0;
  }

  paintInitial(items, totalCount) {
    const grid = this.container.querySelector('#product-grid');
    if (!grid) return;

    grid.replaceChildren(...items.map((item) => item.cloneNode(true)));
    grid.dataset.bucketedCollection = 'true';

    this.container.querySelector('.pagination-wrapper')?.remove();
    this.updateProductCount(totalCount);
    this.ensureLoadMoreSentinel();
    this.refreshAnimations();
  }

  appendItems(items) {
    const grid = this.container.querySelector('#product-grid');
    if (!grid || items.length === 0) return;

    items.forEach((item) => grid.appendChild(item.cloneNode(true)));
    this.refreshAnimations();
  }

  updateProductCount(totalCount) {
    const countText = `${totalCount} ${totalCount === 1 ? 'product' : 'products'}`;
    document.querySelectorAll('#ProductCount, #ProductCountDesktop').forEach((node) => {
      node.textContent = countText;
      node.classList.remove('loading');
    });
  }

  ensureLoadMoreSentinel() {
    let sentinel = this.container.querySelector('[data-bucketed-load-more]');
    if (!sentinel) {
      sentinel = document.createElement('div');
      sentinel.className = 'tmp-bucketed-load-more';
      sentinel.dataset.bucketedLoadMore = 'true';
      sentinel.setAttribute('aria-live', 'polite');
      this.container.querySelector('.collection')?.appendChild(sentinel);
    }
    this.loadMoreSentinel = sentinel;
  }

  updateLoadMoreState() {
    if (!this.loadMoreSentinel) return;

    const hasMore = this.loadedCount < this.totalCount;
    this.loadMoreSentinel.hidden = !hasMore;
    this.loadMoreSentinel.textContent = hasMore ? 'Loading more products...' : '';
    this.container.dataset.bucketedCollectionComplete = hasMore ? 'false' : 'true';

    this.disconnectLoadMore();
    if (!hasMore) return;

    this.loadMoreObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.loadMore();
      },
      { rootMargin: '700px 0px 700px 0px' }
    );
    this.loadMoreObserver.observe(this.loadMoreSentinel);
  }

  disconnectLoadMore() {
    if (!this.loadMoreObserver) return;
    this.loadMoreObserver.disconnect();
    this.loadMoreObserver = undefined;
  }

  async loadMore() {
    if (this.loadingMore || this.rendering || this.loadedCount >= this.totalCount) return;

    this.loadingMore = true;
    this.container.dataset.bucketedCollectionState = 'loading-more';
    this.disconnectLoadMore();

    try {
      const params = this.currentParams();
      const items = await this.getVirtualItems(this.loadedCount, this.pageSize, params);
      this.appendItems(items);
      this.loadedCount += items.length;
    } catch (error) {
      console.warn('Bucketed collection load-more failed:', error);
    } finally {
      this.loadingMore = false;
      this.container.dataset.bucketedCollectionState = 'ready';
      this.updateLoadMoreState();
    }
  }

  refreshAnimations() {
    if (typeof initializeScrollAnimationTrigger === 'function') {
      initializeScrollAnimationTrigger();
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-bucketed-catalog="true"]').forEach((root) => {
    new TmpBucketedCollection(root).connect();
  });
});
